<template>
  <div class="dropdown-menu account-popover" :class="{ 'account-popover-logged-in': isLoggedIn }">
    <div class="dropdown-header">
      <h6>Bem-vindo à <span class="sitename">MX3 Network</span></h6>
      <p class="mb-0">Acesse sua conta e gerencie pedidos</p>
    </div>
    <div class="dropdown-body">
      <!-- Ao clicar no ícone de login no cabeçalho, o próprio dropdown já
           mostra os campos de login (e-mail/senha) em vez de só um botão
           "Entrar" que levava para a página /login. -->
      <div v-if="!isLoggedIn" class="account-login-panel">
        <div class="account-google-wrapper">
          <GoogleLogin />
        </div>
        <div class="account-divider"><span>ou</span></div>

        <form class="account-login-form" @submit.prevent="onSubmit">
          <div class="account-form-group">
            <input
              v-model="email"
              type="email"
              placeholder="E-mail"
              class="account-form-input"
              autocomplete="username"
              required
            />
          </div>

          <div class="account-form-group">
            <div class="account-input-wrapper">
              <input
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Senha"
                class="account-form-input"
                autocomplete="current-password"
                required
              />
              <button
                type="button"
                class="account-toggle-password"
                :aria-label="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
                @click="showPassword = !showPassword"
              >
                <i class="bi" :class="showPassword ? 'bi-eye-slash' : 'bi-eye'" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <div class="account-remember-row">
            <label class="account-remember-label">
              <input type="checkbox" v-model="rememberMe" />
              <span>Lembrar-me</span>
            </label>
            <router-link to="/recovery" class="account-link-forgot">Esqueci minha senha</router-link>
          </div>

          <small v-if="loginError" class="account-login-error" role="alert">{{ loginError }}</small>

          <button type="submit" class="account-action account-action-primary" :disabled="loggingIn">
            {{ loggingIn ? 'Entrando...' : 'Entrar' }}
          </button>
        </form>

        <div class="account-login-links">
          <span>Ainda não tem uma conta?</span>
          <router-link to="/register" class="account-link-register">Criar conta</router-link>
        </div>

        <div class="account-theme-row">
          <ThemeSwitcher :visible="true" />
        </div>
      </div>

      <template v-else>
        <a class="dropdown-item account-item" href="/profile">
          <i class="bi bi-person-circle" aria-hidden="true"></i>
          <span>Meu Perfil</span>
        </a>
        <a class="dropdown-item account-item" href="/orders">
          <i class="bi bi-bag-check" aria-hidden="true"></i>
          <span>Meus Pedidos</span>
          <span class="account-item-count" v-if="pedidosCount">{{ pedidosCount }}</span>
        </a>
        <a class="dropdown-item account-item" href="/wishlist">
          <i class="bi bi-heart" aria-hidden="true"></i>
          <span>Lista de Desejos</span>
          <span class="account-item-count" v-if="desejosCount">{{ desejosCount }}</span>
        </a>
        <button type="button" class="dropdown-item account-item logout-item" @click="logout">
          <i class="bi bi-arrow-left" aria-hidden="true"></i>
          <span>Sair</span>
        </button>
        <!-- Alternância de tema (claro/escuro): fica abaixo do link "Sair",
             como último item do menu da conta, para não competir com as
             ações da conta e ficar sempre visível no rodapé do painel. -->
        <div class="dropdown-item theme-switcher-item theme-switcher-item-bottom">
          <ThemeSwitcher :visible="true" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ThemeSwitcher from "@/components/ThemeSwitcher.vue"
import GoogleLogin from "@/components/GoogleLogin.vue"

export default {
  name: "AccountMenu",
  components: {
    ThemeSwitcher,
    GoogleLogin
  },
  data() {
    return {
      isLoggedIn: false,
      email: "",
      password: "",
      showPassword: false,
      rememberMe: true,
      loginError: "",
      loggingIn: false,
      pedidosCount: 0,
    };
  },
  computed: {
    // Quantidade de itens salvos na Lista de Desejos (mesmo contador
    // global usado pelo coração dos cards de produto).
    desejosCount() {
      return (this.$store.state.desejos || []).length;
    },
  },
  mounted() {
    // Verifica o estado de autenticação do usuário
    this.$root.$on("mx3-orders-updated", this.onOrdersUpdated);
    this.checkAuthStatus();
    if (this.isLoggedIn) {
      this.carregarQuantidadePedidos();
    }
  },
  beforeDestroy() {
    this.$root.$off("mx3-orders-updated", this.onOrdersUpdated);
  },
  methods: {
    // Busca a quantidade de pedidos já realizados pela conta para exibir a
    // contagem ao lado de "Meus Pedidos", no mesmo estilo do contador do
    // carrinho no cabeçalho.
    onOrdersUpdated(event) {
      // Atualização otimista para que o badge mude no mesmo instante em que
      // o checkout conclui; a consulta seguinte confirma o total no servidor.
      if (event && event.created) {
        this.pedidosCount = (Number(this.pedidosCount) || 0) + 1;
      }
      this.carregarQuantidadePedidos();
    },
    carregarQuantidadePedidos() {
      this.$api
        .get(window.location.origin + "/api/order_list.php?_=" + Date.now())
        .then((res) => {
          this.pedidosCount = Array.isArray(res.data) ? res.data.length : 0;
        })
        .catch(() => {
          this.pedidosCount = 0;
        });
    },
    logout(){
      // Remove os dados de sessão/autenticação (mesmo critério usado em
      // api.js: accessToken, addon, isAdmin).
      localStorage.removeItem('accessToken');
      localStorage.removeItem('addon');
      localStorage.removeItem('isAdmin');

      // Limpa o carrinho exibido localmente ao deslogar. O conteúdo não é
      // perdido: fica salvo no servidor vinculado à conta do cliente (ver
      // App.vue -> watch de $store.state.carrinho, que salva a cada
      // mudança via /api/cart_save.php) e é restaurado automaticamente no
      // próximo login com a mesma conta (App.vue -> restaurarCarrinhoDaConta()).
      localStorage.removeItem('itensCarrinho');
      localStorage.removeItem('carrinhoContaRestaurado');
      localStorage.removeItem('itensDesejo');
      this.$store.state.desejos = [];

      this.isLoggedIn = false;
      window.location.href = '/';
    },
    checkAuthStatus() {
      setInterval(() => {
        const logado = !!localStorage.getItem("accessToken");
        if (logado && !this.isLoggedIn) {
          this.carregarQuantidadePedidos();
        }
        this.isLoggedIn = logado;
      }, 500);
    },
    // Mesma lógica de autenticação usada em pages/Login.vue - reaproveitada
    // aqui para que o login funcione direto no dropdown do cabeçalho, sem
    // precisar navegar até a página /login.
    async onSubmit() {
      this.loginError = "";
      this.loggingIn = true;
      try {
        const bodyContent = JSON.stringify({
          username: this.email,
          password: this.password,
        });

        const response = await fetch(window.location.origin + "/api/login.php", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Accept": "*/*",
          },
          body: bodyContent,
        });

        let data;
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.indexOf("application/json") !== -1) {
          data = await response.json();
        } else {
          const text = await response.text();
          throw new Error("Resposta inválida do servidor: " + text.substring(0, 50));
        }

        if (!response.ok) {
          throw new Error(data.detail || data.message || data.error || data.erro || "Erro ao fazer login");
        }

        localStorage.setItem("accessToken", data.access || data.token || "logged_in");
        this.isLoggedIn = true;
        await this.$api.getAddon();
        window.location.reload();
      } catch (err) {
        if (err.message === "No active account found with the given credentials") {
          this.loginError = "Nenhuma conta ativa encontrada com as credenciais fornecidas.";
        } else {
          this.loginError = "Houve um problema ao realizar login, verifique se o usuário e(ou) senha estão corretos.";
        }
      } finally {
        this.loggingIn = false;
      }
    },
  }
}
</script>

<style scoped>
/* Popover maior o suficiente para acomodar o formulário de login inline,
   nas cores do tema ativo da MX3 (variáveis já usadas no restante do
   e-commerce, em vez das cores fixas do e-commerce de referência). */
.account-popover {
  /* Antes do login, mantém espaço suficiente para o formulário completo. */
  width: min(100vw - 24px, 400px);
  min-width: 300px;
  padding: 0 !important;
  overflow: hidden;
  border: 1px solid var(--mx3-style-primary, #00d9ff) !important;
  border-radius: 16px !important;
  background: var(--mx3-style-bg-card, #161b26) !important;
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.42) !important;
}

/* Depois do login, o menu contém somente os links e fica mais compacto. */
.account-popover.account-popover-logged-in {
  width: min(100vw - 24px, 320px);
  min-width: 280px;
}

.account-popover .dropdown-header {
  padding: 18px 22px 16px;
  /* Base escura fixa + gradiente nas cores do tema: garante contraste alto
     para o texto branco em qualquer tema (o accent_color do tema claro é
     mais escuro que o do tema escuro, e texto preto sobre ele ficava com
     baixo contraste). */
  background: linear-gradient(135deg, #0a0e1b 0%, var(--mx3-style-primary-dark, #0099cc) 55%, var(--mx3-style-primary, #00d9ff) 100%);
  color: #ffffff;
}

.account-popover .dropdown-header h6 {
  margin: 0 0 4px;
  color: #ffffff;
  font-size: 1.05rem;
  font-weight: 600;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

.account-popover .dropdown-header .sitename {
  color: var(--mx3-style-primary-light, #33e0ff);
  font-weight: 800;
}

.account-popover .dropdown-header p {
  margin: 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.92rem;
}

.account-popover .dropdown-body {
  padding: 18px 0 10px;
  background: var(--mx3-style-bg-card, #161b26);
}

.theme-switcher-item {
  padding: 0.5rem 1rem 0.75rem;
}
.theme-switcher-item :deep(.theme-switcher-btn) {
  width: 100%;
  justify-content: center;
}

/* Variante de rodapé: usada quando o usuário está logado, abaixo do item
   "Sair". Ganha uma linha divisória acima (mesmo padrão visual do
   account-theme-row do painel de login) para se destacar como a última
   ação do menu, em vez de se misturar com os itens de navegação. */
.theme-switcher-item-bottom {
  margin-top: 4px;
  padding: 12px 22px 10px;
  border-top: 1px solid var(--mx3-style-border, rgba(255, 255, 255, 0.08));
}

.account-popover .account-theme-row {
  display: flex;
  justify-content: center;
  margin: 4px 0 0;
  /* Sem padding horizontal próprio: o recuo de 22px do painel já define
     a largura útil - era o padding extra aqui que deixava o botão de
     alternância de tema mais estreito que os campos de e-mail/senha. */
  padding: 12px 0 0;
  border-top: 1px solid var(--mx3-style-border, rgba(255, 255, 255, 0.08));
}

/* Mesmo tamanho horizontal E vertical dos campos de e-mail/senha e do
   botão "Entrar" ao lado. O ThemeSwitcher renderiza um <div> intermediário
   (.theme-switcher) entre a linha e o botão - sem esticá-lo, o botão
   "width:100%" ficava 100% do DIV (largura do próprio texto), mais
   estreito que os campos. */
.account-popover .account-theme-row :deep(.theme-switcher) {
  width: 100%;
}

.account-popover .account-theme-row :deep(.theme-switcher-btn) {
  box-sizing: border-box;
  width: 100%;
  min-height: 44px;
  justify-content: center;
}

.account-popover .account-item {
  display: flex;
  width: 100%;
  min-height: 44px;
  align-items: center;
  gap: 12px;
  padding: 10px 22px;
  border: 0;
  color: var(--mx3-style-primary, #00d9ff) !important;
  background: transparent !important;
  font-size: 0.95rem;
  text-align: left;
  text-decoration: none;
}

.account-popover .account-item i {
  width: 20px;
  color: var(--mx3-style-primary, #00d9ff);
  font-size: 1.05rem;
  text-align: center;
}

/* Contador de itens (Meus Pedidos / Lista de Desejos), no mesmo espírito
   do badge do carrinho no cabeçalho: pílula compacta na cor de destaque
   do tema, alinhada à direita do item. */
.account-popover .account-item .account-item-count {
  margin-left: 8px;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--mx3-style-primary, #00d9ff) 0%, var(--mx3-style-primary-dark, #0099cc) 100%);
  /* Preto fixo (não a variável --mx3-style-bg-darker, que é #ffffff no
     tema claro e uma cor quase-preta diferente no escuro) - mesmo
     critério do selo do carrinho no cabeçalho (Header.vue/HeaderMobile.vue
     usam a classe .text-black-full, "color: black !important", nos dois
     temas). O fundo em degradê já vem da cor de destaque gerenciada em
     Personalizar; só o texto do número é sempre preto, como no carrinho. */
  color: #000000;
  font-size: 0.75rem;
  font-weight: 800;
  line-height: 1;
}

.account-popover .account-item:hover,
.account-popover .account-item:focus-visible {
  color: var(--mx3-style-primary-light, #33e0ff) !important;
  outline: none;
}

.account-popover .logout-item {
  cursor: pointer;
}

.account-popover .account-login-panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 22px 6px;
}

.account-popover .account-google-wrapper {
  display: flex;
  justify-content: center;
}

.account-popover .account-google-wrapper :deep(.g-id-slot) {
  width: 100% !important;
}

.account-popover .account-divider {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--mx3-style-text-muted, rgba(255, 255, 255, 0.4));
  font-size: 0.76rem;
  text-transform: uppercase;
}

.account-popover .account-divider::before,
.account-popover .account-divider::after {
  flex: 1 1 auto;
  height: 1px;
  background: var(--mx3-style-border, rgba(255, 255, 255, 0.14));
  content: "";
}

.account-popover .account-divider span {
  padding: 0 10px;
}

.account-popover .account-login-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.account-popover .account-form-group {
  display: flex;
  flex-direction: column;
}

.account-popover .account-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.account-popover .account-form-input {
  width: 100%;
  min-height: 44px;
  padding: 10px 40px 10px 14px;
  /* Cores do tema ativo (o tema claro herdava fundo/borda/texto do
     tema escuro por causa dos hexes fixos). */
  border: 1px solid var(--mx3-style-border, #38526a);
  border-radius: 10px;
  outline: none;
  background: var(--mx3-style-input-bg, #142333);
  color: var(--mx3-style-input-text, #fff);
  /* 16px evita o auto-zoom do iOS ao focar (com o zoom por pinça
     liberado no viewport, o Safari ampliava a página ao tocar o campo). */
  font-size: 16px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.account-popover .account-form-input::placeholder {
  color: var(--mx3-style-text-muted, #8ea2b5);
}

/* Foco: mesma borda usada no restante do e-commerce (campo "Buscar
   produtos" e formulário de cotação B2B), na cor de destaque do tema. */
.account-popover .account-form-input:focus {
  border: var(--mx3-style-primary, #00d9ff) solid 2px;
  padding: 9px 39px 9px 13px;
  box-shadow: none;
}

.account-popover .account-toggle-password {
  position: absolute;
  top: 50%;
  right: 8px;
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #8ea2b5;
  cursor: pointer;
  transform: translateY(-50%);
}

.account-popover .account-toggle-password:hover,
.account-popover .account-toggle-password:focus-visible {
  color: var(--mx3-style-primary, #00d9ff);
  outline: none;
}

.account-popover .account-remember-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 0.8rem;
}

.account-popover .account-remember-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--mx3-style-text-secondary, rgba(255, 255, 255, 0.75));
  cursor: pointer;
}

.account-popover .account-remember-label input {
  accent-color: var(--mx3-style-primary, #00d9ff);
}

.account-popover .account-link-forgot,
.account-popover .account-link-register {
  color: var(--mx3-style-primary, #00d9ff);
  font-weight: 600;
  text-decoration: none;
}

.account-popover .account-link-forgot:hover,
.account-popover .account-link-register:hover {
  color: var(--mx3-style-primary-light, #33e0ff);
  text-decoration: underline;
}

.account-popover .account-login-error {
  color: #ff8a80;
  font-size: 0.8rem;
  line-height: 1.4;
}

.account-popover .account-login-links {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 0.82rem;
  color: var(--mx3-style-text-muted, rgba(255, 255, 255, 0.6));
}

.account-popover .account-action {
  width: 100%;
  min-height: 44px;
  padding: 10px 16px;
  border-radius: 22px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 700;
  transition: transform 0.2s ease, filter 0.2s ease;
}

.account-popover .account-action:hover:not(:disabled) {
  filter: brightness(1.08);
  transform: translateY(-1px);
}

.account-popover .account-action:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.account-popover .account-action-primary {
  border: none;
  background: linear-gradient(135deg, var(--mx3-style-primary, #00d9ff) 0%, var(--mx3-style-primary-dark, #0099cc) 100%);
  color: #03121f;
}

@media (max-width: 520px) {
  .account-popover,
  .account-popover.account-popover-logged-in {
    min-width: 0;
    width: calc(100vw - 24px);
  }
}
</style>
